import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User, Phone, Mail, MessageSquare } from 'lucide-react';

const automaticReplies = {
  default: "Welcome to TaxEases! How can we assist you with your tax-related queries today?",
  itr: "For ITR filing, we offer comprehensive services to ensure accurate and timely submission. Would you like to schedule a consultation with one of our tax experts?",
  gst: "Our GST services cover registration, filing, and compliance. Can you specify which aspect of GST you need help with? We'd be happy to arrange a call with our GST specialist.",
  planning: "Tax planning is crucial for optimizing your financial position. Our experts can provide personalized strategies. Would you like us to contact you for a detailed discussion?",
  audit: "We provide robust audit defense services. Have you received an audit notice, or are you looking for preventive measures? Let's schedule a call to discuss your situation.",
  offline: "We understand you prefer offline communication. Please provide your preferred contact method (phone or email) and the best time to reach you. Our team will get in touch within 24 hours.",
};

const EnquiryForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [contactMethod, setContactMethod] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim() && !contactInfo) return;

    let userMessage = { text: inputValue, sender: 'user' };
    if (contactInfo) {
      userMessage = { text: `${contactMethod}: ${contactInfo}`, sender: 'user' };
      setContactMethod('');
      setContactInfo('');
    }
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Simulate automatic reply
    setTimeout(() => {
      let replyText = automaticReplies.default;
      const lowercaseInput = inputValue.toLowerCase();
      if (lowercaseInput.includes('itr') || lowercaseInput.includes('income tax')) {
        replyText = automaticReplies.itr;
      } else if (lowercaseInput.includes('gst')) {
        replyText = automaticReplies.gst;
      } else if (lowercaseInput.includes('planning') || lowercaseInput.includes('strategy')) {
        replyText = automaticReplies.planning;
      } else if (lowercaseInput.includes('audit')) {
        replyText = automaticReplies.audit;
      } else if (lowercaseInput.includes('call') || lowercaseInput.includes('phone') || lowercaseInput.includes('email')) {
        replyText = automaticReplies.offline;
      }
      const botMessage = { text: replyText, sender: 'bot' };
      setMessages(prev => [...prev, botMessage]);
    }, 1000);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50  ">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="bg-white rounded-lg shadow-2xl w-80 sm:w-96 overflow-hidden"
            style={{borderRadius:'12px'}}
          >
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-4 flex justify-between items-center">
              <h3 className="text-white font-semibold text-lg">TaxEases Support</h3>
              <button onClick={() => setIsOpen(false)} className="text-cyan-500 font-bold border-cyan-500 rounded-full p-1 hover:text-red-500 hover:border-red-500 transition-colors border">
                <X size={20} />
              </button>
            </div>
            <div className="h-96 overflow-y-auto p-4 bg-gray-50">
              {messages.map((message, index) => (
                <div key={index} className={`mb-4 ${message.sender === 'user' ? 'text-right' : 'text-left'}`}>
                  <div className={`inline-block p-3 rounded-lg max-w-[80%] ${
                    message.sender === 'user' 
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' 
                      : 'bg-white text-gray-800 shadow-md'
                  }`} style={{borderRadius:'15px'}}>
                    {message.text}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <form onSubmit={handleSubmit} className="p-4 bg-white border-t border-gray-200">
              {contactMethod ? (
                <div className="flex items-center mb-2">
                  <input
                    type={contactMethod === 'Phone' ? 'tel' : 'email'}
                    value={contactInfo}
                    onChange={(e) => setContactInfo(e.target.value)}
                    placeholder={`Enter your ${contactMethod.toLowerCase()}`}
                    className="flex-grow p-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{borderRadius:'50%'}}
                  />
                  <button type="submit" className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white p-2 rounded-r-lg hover:from-cyan-600 hover:to-blue-600 transition-colors">
                    <Send size={20} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Type your query here..."
                    className="flex-grow p-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{borderRadius:'25px'}}
                  />
                  <button type="submit" className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white p-2 rounded-r-lg hover:from-cyan-600 hover:to-blue-600 transition-colors" style={{borderRadius:'100%'}}>
                    <Send size={20} />
                  </button>
                </div>
              )}
            </form>
            <div className="p-2 bg-gray-100 flex justify-center space-x-4">
              <button onClick={() => setContactMethod('Phone')} className="text-cyan-600 hover:text-cyan-700 transition-colors">
                <Phone size={20} />
              </button>
              <button onClick={() => setContactMethod('Email')} className="text-cyan-600 hover:text-cyan-700 transition-colors">
                <Mail size={20} />
              </button>
              <button onClick={() => setContactMethod('')} className="text-cyan-600 hover:text-cyan-700 transition-colors">
                <MessageSquare size={20} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!isOpen && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(true)}
          className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <User size={24} />
        </motion.button>
      )}
    </div>
  );
};

export default EnquiryForm;

